import React, {useCallback, useEffect, useState, useImperativeHandle, forwardRef} from 'react';
import {find} from 'lodash/collection';
import {difference, flattenDeep, uniq} from 'lodash/array';
import RouteTitle from '../../components/RouteTitle';
import {toHrsMinsSecs, toTime} from '../../libs/formatLib';
import LoadMessage from '../../components/LoadMessage';
import {stopModelData, transferModelData} from '../../services/ModelService';
import {ulid} from 'ulid';
import {RetweetOutlined, WarningFilled} from '@ant-design/icons';
import {Button, Input, Switch, Table, Tag, Tooltip} from 'antd';
import {ThunderboltOutlined} from '@ant-design/icons';
import {BaseStop, BusRoute, Stop} from '../../model/busRoute';
import {values} from 'lodash/object';
import {differenceWith, uniqWith, intersection} from 'lodash';
import useAllRoutes from '../../hooks/useAllRoutes';
import useAllSchedules from '../../hooks/useAllSchedules';
import './TransfersV2.css';

export const isSetdownTransfer = (transfer, t) => {
    return t.fromRouteId === transfer.fromRouteId && t.fromTripId === transfer.fromTripId && t.fromStopId === transfer.fromStopId && t.fromStopTimeId === transfer.fromStopTimeId;
};

export const isEqualTransfer = (transfer, t) => {
    return t.fromRouteId === transfer.fromRouteId && t.fromTripId === transfer.fromTripId && t.fromStopId === transfer.fromStopId && t.fromStopTimeId === transfer.fromStopTimeId && t.toRouteId === transfer.toRouteId && t.toTripId === transfer.toTripId && t.toStopId === transfer.toStopId && t.toStopTimeId === transfer.toStopTimeId;
};

const findTransfer = (transfers, transfer) => {
    return find(transfers, t => {
        return isEqualTransfer(transfer, t);
    });
};

const validateScheduleIntersection = (transfer, allRoutes, allSchedules, intersectingDays = null) => {
    if (!transfer.fromRouteId || !transfer.fromTripId || !transfer.toRouteId || !transfer.toTripId) {
        return null;
    }

    const fromRoute = allRoutes[transfer.fromRouteId];
    const toRoute = allRoutes[transfer.toRouteId];

    if (!fromRoute || !toRoute) {
        return null;
    }

    const fromTrip = find(fromRoute?.services, {tripId: transfer.fromTripId});
    const toTrip = find(toRoute?.services, {tripId: transfer.toTripId});

    if (!fromTrip?.scheduleIds || !toTrip?.scheduleIds) {
        return null;
    }

    // If we already calculated intersecting days and it's > 0, no warning needed
    if (intersectingDays !== null && intersectingDays > 0) {
        return null;
    }

    // Quick check: if they share any scheduleId, they definitely intersect
    const sharedScheduleIds = intersection(fromTrip.scheduleIds, toTrip.scheduleIds);
    if (sharedScheduleIds.length > 0) {
        return null;
    }

    // If no shared scheduleIds, check if actual dates intersect
    if (allSchedules) {
        const fromSchedules = fromTrip.scheduleIds.map(id => allSchedules[id]).filter(s => !!s);
        const toSchedules = toTrip.scheduleIds.map(id => allSchedules[id]).filter(s => !!s);

        if (fromSchedules.length && toSchedules.length) {
            // Check if date ranges overlap at all (cheap check first)
            const fromFirstDate = fromSchedules.reduce((earliest, s) => {
                const first = s.getFirstActiveDate?.();
                return first && (!earliest || first.isBefore(earliest)) ? first : earliest;
            }, null);
            const fromLastDate = fromSchedules.reduce((latest, s) => {
                const last = s.getLastActiveDate?.();
                return last && (!latest || last.isAfter(latest)) ? last : latest;
            }, null);
            const toFirstDate = toSchedules.reduce((earliest, s) => {
                const first = s.getFirstActiveDate?.();
                return first && (!earliest || first.isBefore(earliest)) ? first : earliest;
            }, null);
            const toLastDate = toSchedules.reduce((latest, s) => {
                const last = s.getLastActiveDate?.();
                return last && (!latest || last.isAfter(latest)) ? last : latest;
            }, null);

            // If date ranges don't overlap at all, no intersection possible
            if (fromFirstDate && fromLastDate && toFirstDate && toLastDate) {
                if (fromLastDate.isBefore(toFirstDate, 'day') || toLastDate.isBefore(fromFirstDate, 'day')) {
                    return 'No common transfer dates';
                }
            }

            // Date ranges overlap - need to check actual intersecting days
            // If intersectingDays was calculated and is 0, warn
            if (intersectingDays === 0) {
                return 'No common transfer dates';
            }
        }
    }

    // If we can't determine, no warning (benefit of the doubt)
    return null;
};

// Helper to get date range bounds for schedules
const getScheduleDateBounds = (schedules) => {
    if (!schedules?.length) return null;

    const firstDate = schedules.reduce((earliest, s) => {
        const first = s.getFirstActiveDate?.();
        return first && (!earliest || first.isBefore(earliest)) ? first : earliest;
    }, null);
    const lastDate = schedules.reduce((latest, s) => {
        const last = s.getLastActiveDate?.();
        return last && (!latest || last.isAfter(latest)) ? last : latest;
    }, null);

    return firstDate && lastDate ? {firstDate, lastDate} : null;
};

// Fast check: returns true if ANY intersecting day exists (exits early on first match)
// Used for initial load to show "Available" status without expensive full calculation
const hasAnyIntersectingDay = (fromSchedules, toSchedules) => {
    if (!fromSchedules?.length || !toSchedules?.length) return null;

    const fromBounds = getScheduleDateBounds(fromSchedules);
    const toBounds = getScheduleDateBounds(toSchedules);

    if (!fromBounds || !toBounds) return null;

    // Find the overlap range
    const overlapStart = fromBounds.firstDate.isAfter(toBounds.firstDate) ? fromBounds.firstDate : toBounds.firstDate;
    const overlapEnd = fromBounds.lastDate.isBefore(toBounds.lastDate) ? fromBounds.lastDate : toBounds.lastDate;

    // No overlap at all
    if (overlapEnd.isBefore(overlapStart, 'day')) return false;

    // Check up to 365 days, exit on first match
    const maxDays = Math.min(overlapEnd.diff(overlapStart, 'day') + 1, 365);
    for (let i = 0; i < maxDays; i++) {
        const date = overlapStart.add(i, 'day');
        const fromActive = fromSchedules.some(s => s.isActive?.(date));
        const toActive = toSchedules.some(s => s.isActive?.(date));
        if (fromActive && toActive) {
            return true; // Exit early on first match
        }
    }
    return false;
};

// Full calculation: returns { count, dates } - only called when user enables a transfer
// This is expensive but only runs on-demand for a single transfer pair
const getIntersectingDaysInfo = (fromSchedules, toSchedules) => {
    if (!fromSchedules?.length || !toSchedules?.length) return null;

    const fromBounds = getScheduleDateBounds(fromSchedules);
    const toBounds = getScheduleDateBounds(toSchedules);

    if (!fromBounds || !toBounds) return null;

    // Find the overlap range
    const overlapStart = fromBounds.firstDate.isAfter(toBounds.firstDate) ? fromBounds.firstDate : toBounds.firstDate;
    const overlapEnd = fromBounds.lastDate.isBefore(toBounds.lastDate) ? fromBounds.lastDate : toBounds.lastDate;

    if (overlapEnd.isBefore(overlapStart, 'day')) return {count: 0, dates: []};

    // Count days where both schedules are active (limit to 365 days for performance)
    const maxDays = Math.min(overlapEnd.diff(overlapStart, 'day') + 1, 365);
    let count = 0;
    const dates = [];
    const maxDatesToCollect = 20; // Limit dates for tooltip display

    for (let i = 0; i < maxDays; i++) {
        const date = overlapStart.add(i, 'day');
        const fromActive = fromSchedules.some(s => s.isActive?.(date));
        const toActive = toSchedules.some(s => s.isActive?.(date));
        if (fromActive && toActive) {
            count++;
            if (dates.length < maxDatesToCollect) {
                dates.push(date.format('DD/MM/YYYY'));
            }
        }
    }

    return {count, dates};
};

const getScheduleStatus = (schedule) => {
    if (!schedule) return null;
    if (schedule.isFuture?.()) return 'Future';
    if (schedule.isObsolete?.()) return 'Past';
    return 'Active';
};

const getScheduleStatusColor = (status) => {
    switch (status) {
        case 'Future': return 'processing';
        case 'Past': return 'default';
        case 'Active': return 'success';
        default: return 'default';
    }
};

const getTripScheduleInfo = (tripId, routeId, allRoutes, allSchedules, setdownSchedules = null, isEnabled = false) => {
    if (!allRoutes || !allSchedules || !tripId || !routeId) return null;

    const route = allRoutes[routeId];
    const trip = find(route?.services, {tripId});

    if (!trip?.scheduleIds?.length) return null;

    // Get all schedules for this trip
    const tripSchedules = trip.scheduleIds
        .map(sId => allSchedules[sId])
        .filter(s => !!s);

    if (!tripSchedules.length) return null;

    let hasActive = false;
    let hasFuture = false;
    let hasPast = false;

    // Collect schedule names and determine overall status
    const scheduleNames = tripSchedules.map(schedule => {
        const status = getScheduleStatus(schedule);

        if (status === 'Active') hasActive = true;
        if (status === 'Future') hasFuture = true;
        if (status === 'Past') hasPast = true;

        return schedule.scheduleName;
    }).filter(name => !!name);

    // Determine overall status
    let status = 'Active';
    if (hasActive) {
        status = 'Active';
    } else if (hasFuture && !hasPast) {
        status = 'Future';
    } else if (hasPast && !hasFuture) {
        status = 'Past';
    }

    // Check schedule intersection if setdown schedules provided
    let hasIntersection = null; // null = unknown, true = available, false = no intersection
    let intersectingDays = null;
    let intersectingDates = [];

    if (setdownSchedules?.length) {
        if (isEnabled) {
            // Full calculation only for enabled transfers (user clicked to enable)
            const intersectionInfo = getIntersectingDaysInfo(setdownSchedules, tripSchedules);
            intersectingDays = intersectionInfo?.count ?? null;
            intersectingDates = intersectionInfo?.dates ?? [];
            hasIntersection = intersectingDays > 0;
        } else {
            // Fast check for non-enabled transfers - just determine if ANY intersection exists
            hasIntersection = hasAnyIntersectingDay(setdownSchedules, tripSchedules);
        }
    }

    return {
        scheduleNames,
        status,
        scheduleCount: tripSchedules.length,
        hasIntersection,
        intersectingDays,
        intersectingDates,
        tripSchedules
    };
};

const getSetdownDataForStop = (stop, transfers) => {
    let data = [];
    transfers?.filter(t => t.fromStopId === stop.stopId)?.forEach(transfer => {
        const existing = find(data, s => isSetdownTransfer(transfer, s.transfer));
        if (existing) {
            existing.count++;
        } else {
            const fromRoute = find(stop.getAllRoutes(), ['routeId', transfer.fromRouteId]);
            data.push(({
                key: `${transfer.fromRouteId}|${transfer.fromTripId}|${transfer.fromStopTimeId}`,
                service: <RouteTitle
                    route={fromRoute || {
                        routeId: transfer.fromRouteId, routeNumber: transfer.fromRouteNumber
                    }} link={true} meta={true}/>,
                arrive: toTime(transfer.arriveSecs, true),
                arriveSecs: transfer.arriveSecs,
                routeNumber: transfer.fromRouteNumber,
                transfer,
                count: 1
            }));
        }
    });

    data = data.concat(flattenDeep(stop.routes.filter(stopRoute => stopRoute.services).map(stopRoute => {
        return Object.keys(stopRoute.services).map(tripId => stopRoute.services[tripId].passingTimes.map(pt => {
            const transfer = {
                fromStopId: stop.stopId,
                fromRouteId: stopRoute.routeId,
                fromTripId: tripId,
                fromStopTimeId: pt.stopTimeId,
                fromColour: stopRoute.colour,
                fromRouteNumber: stopRoute.routeNumber
            };
            const existing = find(data, s => isSetdownTransfer(transfer, s.transfer));
            if (!existing) {
                return {
                    key: `${stopRoute.routeId}|${tripId}|${pt.stopTimeId}`,
                    service: <RouteTitle route={stopRoute || {
                        routeId: transfer.toRouteId, routeNumber: transfer.toRouteNumber
                    }} link={true} meta={true}/>,
                    arrive: toTime(pt.arriveSecs, true),
                    arriveSecs: pt.arriveSecs,
                    routeNumber: stopRoute.routeNumber,
                    transfer,
                    count: 0
                };
            }
            return null;
        }));
    }))).filter(d => !!d);
    return data;
};

const TransfersV2 = forwardRef(({
                       point, routeId, tripId, stopTimeId
                   }, ref) => {

    const [allStops, setAllStops] = useState(null);
    const [transfers, setTransfers] = useState(null);
    const [originalTransfers, setOriginalTransfers] = useState(null); // Store original state for cancel

    const [setdownPoint, setSetdownPoint] = useState(new BaseStop(point));

    const [selectedSetdownServices, setSelectedSetdownServices] = useState([]); // "routeId|tripId|stopTimeId"
    const [selectedPickupServices, setSelectedPickupServices] = useState([]); //  "transferId"
    const [originalSelectedPickupServices, setOriginalSelectedPickupServices] = useState([]); // Store original selection

    const [setdownServiceData, setSetdownServiceData] = useState([]);
    const [pickupServiceData, setPickupServiceData] = useState([]);

    const [saving, setSaving] = useState(false);
    const [pendingInSeat, setPendingInSeat] = useState({}); // {transferId: boolean}
    const [pendingTransfers, setPendingTransfers] = useState({}); // {transferId: transfer} - new transfers to add

    const {allRoutes} = useAllRoutes();
    const {schedules: allSchedules} = useAllSchedules();
    const [generatingForService, setGeneratingForService] = useState(null); // key of service currently generating
    const [perServiceTransferCounts, setPerServiceTransferCounts] = useState({}); // {serviceKey: {total, available}}
    const [transferWindow, setTransferWindow] = useState(5); // Global transfer window in minutes
    const [showAllPickups, setShowAllPickups] = useState(false); // Filter to show only available transfers

    // Track if there are unsaved changes
    const hasChanges = useCallback(() => {
        // Check if selection changed
        const originalSet = new Set(originalSelectedPickupServices);
        const currentSet = new Set(selectedPickupServices);
        if (originalSet.size !== currentSet.size) return true;
        for (const id of originalSet) {
            if (!currentSet.has(id)) return true;
        }
        for (const id of currentSet) {
            if (!originalSet.has(id)) return true;
        }
        // Check if in-seat values changed
        if (Object.keys(pendingInSeat).length > 0) return true;
        return false;
    }, [originalSelectedPickupServices, selectedPickupServices, pendingInSeat]);

    useEffect(() => {
        console.log('Transfers loaded effect');
        const time = Date.now();
        const stopsLoadedFn = stops => setAllStops(stops);
        const transfersLoadedFn = transfers => {
            if (typeof transfers === 'function') {
                setTransfers(prevTransfers => {
                    const newTransfers = transfers(prevTransfers);
                    return newTransfers;
                });
            } else {
                setTransfers(Object.values(transfers));
            }
        };
        const listeners = {stops: null, transfers: null};
        // Load existing transfers
        const load = async () => {
            listeners.stops = stopModelData.addListener({loaded: stopsLoadedFn, setterFn: stopsLoadedFn});
            setAllStops(await stopModelData.getAll());
            listeners.transfers = transferModelData.addListener({
                loaded: transfersLoadedFn, setterFn: transfersLoadedFn
            });
            const loadedTransfers = await transferModelData.getAll({asArray: true});
            setTransfers(loadedTransfers);
            setOriginalTransfers(loadedTransfers);
        };

        load().then(() => console.log('Transfers/Stops loaded.'));

        console.log('Transfers loaded effect time', Date.now() - time);
        return () => {
            stopModelData.removeListener(listeners.stops);
            transferModelData.removeListener(listeners.transfers);
        };

    }, []);

    useEffect(() => {
        if (!point || !allStops) {
            return;
        }
        setSetdownPoint(allStops[point.stopId]);
        // setTransferWindow(allStops[point.stopId]?.transferWindow || 0);
    }, [allStops, setSetdownPoint, point]);

    const pickupTableColumns = useCallback(() => {
        return [{
            title: 'Pickup Service',
            dataIndex: 'service',
            key: 'service',
            sorter: (a, b) => a.routeNumber.localeCompare(b.routeNumber),
        }, {
            title: 'Depart',
            dataIndex: 'depart',
            key: 'depart',
            width: 90,
            sorter: (a, b) => a.departSecs - b.departSecs,
        }, {
            title: 'Schedule',
            dataIndex: 'scheduleInfo',
            key: 'scheduleInfo',
            width: 85,
            sorter: (a, b) => {
                const statusOrder = {Active: 0, Future: 1, Past: 2};
                return (statusOrder[a.scheduleInfo?.status] ?? 3) - (statusOrder[b.scheduleInfo?.status] ?? 3);
            },
            render: (scheduleInfo) => {
                if (!scheduleInfo) return <span style={{color: '#999'}}>-</span>;
                const {scheduleNames, status} = scheduleInfo;
                const scheduleTooltip = scheduleNames?.join(', ');
                return (
                    <Tooltip title={scheduleTooltip}>
                        <Tag color={getScheduleStatusColor(status)} style={{margin: 0, cursor: 'default'}}>
                            {status}
                        </Tag>
                    </Tooltip>
                );
            }
        }, {
            title: 'Wait',
            dataIndex: 'dwell',
            key: 'dwell',
            width: 70,
            defaultSortOrder: 'ascend',
            sorter: (a, b) => a.dwellSecs - b.dwellSecs,
            render: (_, record) => record.dwell || '0m'
        }, {
            title: 'In-seat',
            dataIndex: 'inseat',
            key: 'inseat',
            width: 70,
            sorter: (a, b) => (!!a?.transfer?.inSeat === !!b?.transfer?.inSeat) ? 0 : !!a?.transfer?.inSeat ? -1 : 1,
            render: (_, record) => {
                const inSeatValue = pendingInSeat.hasOwnProperty(record.key)
                    ? pendingInSeat[record.key]
                    : record.transfer.inSeat;
                return <Switch
                    disabled={!selectedPickupServices.includes(record.key)}
                    checked={inSeatValue}
                    onChange={(value, e) => {
                        e.stopPropagation();
                        setPendingInSeat(prev => ({...prev, [record.key]: value}));
                    }}/>;
            },
        }, {
            title: 'Dates',
            dataIndex: 'scheduleInfo',
            key: 'days',
            width: 80,
            sorter: (a, b) => {
                // Sort by: hasIntersection (true first), then by intersectingDays if available
                const aVal = a.scheduleInfo?.intersectingDays ?? (a.scheduleInfo?.hasIntersection === true ? 1 : a.scheduleInfo?.hasIntersection === false ? 0 : -1);
                const bVal = b.scheduleInfo?.intersectingDays ?? (b.scheduleInfo?.hasIntersection === true ? 1 : b.scheduleInfo?.hasIntersection === false ? 0 : -1);
                return bVal - aVal;
            },
            render: (scheduleInfo, record) => {
                const {hasIntersection, intersectingDays, intersectingDates} = scheduleInfo || {};
                const hasWarnings = record.transfer.warnings?.length > 0;
                const hasTimingError = record.dwellSecs < 0;
                const isEnabled = selectedPickupServices.includes(record.key);

                // If there's a timing error (negative dwell time)
                if (hasTimingError) {
                    return (
                        <Tooltip title={
                            <div style={{textAlign: 'left'}}>
                                <div style={{fontWeight: 600, marginBottom: 4, color: '#ff4d4f'}}>Invalid Transfer Timing</div>
                                <div style={{fontSize: 12}}>
                                    {record.transfer.toRouteNumber} departs {toHrsMinsSecs(Math.abs(record.dwellSecs), true, true)} before setdown
                                </div>
                                <div style={{fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 4}}>REF-028</div>
                            </div>
                        }>
                            <WarningFilled style={{color: '#dc3545', cursor: 'help'}}/>
                        </Tooltip>
                    );
                }

                // If there are schedule warnings (no common dates)
                if (hasWarnings) {
                    return (
                        <Tooltip title={
                            <div style={{textAlign: 'left'}}>
                                <div style={{fontWeight: 600, marginBottom: 4, color: '#ff4d4f'}}>Schedule Warning</div>
                                {uniq(record.transfer.warnings).map((w, i) => (
                                    <div key={i} style={{fontSize: 12}}>{w}</div>
                                ))}
                            </div>
                        }>
                            <WarningFilled style={{color: '#dc3545', cursor: 'help'}}/>
                        </Tooltip>
                    );
                }

                // For enabled transfers, show full days count with tooltip
                if (isEnabled && intersectingDays !== null && intersectingDays !== undefined) {
                    const isZero = intersectingDays === 0;
                    const datesContent = intersectingDays === 0 ? (
                        <div style={{color: '#ff4d4f', fontWeight: 500}}>No common transfer dates</div>
                    ) : (
                        <div style={{textAlign: 'left'}}>
                            <div style={{fontWeight: 600, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.2)'}}>
                                {intersectingDays} Transfer Date{intersectingDays === 1 ? '' : 's'}
                            </div>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 280}}>
                                {intersectingDates?.map((date, i) => (
                                    <span key={i} style={{
                                        fontSize: 11,
                                        backgroundColor: 'rgba(255,255,255,0.15)',
                                        padding: '2px 6px',
                                        borderRadius: 3
                                    }}>{date}</span>
                                ))}
                                {intersectingDays > (intersectingDates?.length || 0) && (
                                    <span style={{fontSize: 11, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic'}}>
                                        +{intersectingDays - intersectingDates.length} more
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                    return (
                        <Tooltip title={datesContent} overlayStyle={{maxWidth: 320}}>
                            <span style={{fontWeight: 'bold', color: isZero ? '#dc3545' : '#52c41a'}}>
                                {intersectingDays}
                            </span>
                        </Tooltip>
                    );
                }

                // For non-enabled transfers, show "Available" or warning based on fast check
                if (hasIntersection === true) {
                    return (
                        <Tooltip title="Schedule dates overlap - transfer available">
                            <Tag color="success" style={{margin: 0, fontSize: 11}}>Available</Tag>
                        </Tooltip>
                    );
                }

                if (hasIntersection === false) {
                    return (
                        <Tooltip title="No common transfer dates between schedules">
                            <Tag color="error" style={{margin: 0, fontSize: 11}}>Not available</Tag>
                        </Tooltip>
                    );
                }

                return <span style={{color: '#999'}}>-</span>;
            }
        }];
    }, [selectedPickupServices, saving]);

    useEffect(() => {
        if (!setdownPoint || !transfers) {
            setSetdownServiceData([]);
            return;
        }

        setdownPoint.setLinkedStops(allStops);

        const tableData = getSetdownDataForStop(setdownPoint, transfers);

        // Add schedule info to each row
        tableData.forEach(row => {
            row.scheduleInfo = getTripScheduleInfo(
                row.transfer.fromTripId,
                row.transfer.fromRouteId,
                allRoutes,
                allSchedules
            );
        });

        if (routeId?.length && tripId?.length && stopTimeId?.length) {
            const key = `${routeId}|${tripId}|${stopTimeId}`;
            const idx = tableData.findIndex(s => s.key === key);
            if (idx > -1) {
                const [service] = tableData.splice(idx, 1);
                setSelectedSetdownServices([key]);
                tableData.unshift(service);
            }
        }
        setSetdownServiceData(tableData);

    }, [setdownPoint, transfers, setSetdownServiceData, setSelectedSetdownServices, routeId, tripId, stopTimeId, allStops, allRoutes, allSchedules]);


    const addPickupDataForStop = (data, stop, setdownService, transfers, selected, allRoutes, allSchedules) => {
        // Get setdown schedules for calculating intersecting days
        const setdownSchedules = setdownService?.scheduleInfo?.tripSchedules || [];

        transfers.forEach(transfer => {
            if (transfer.toStopId === stop.stopId && isSetdownTransfer(transfer, setdownService.transfer)) {
                const toRoute = find(stop.getAllRoutes(), ['routeId', transfer.toRouteId]);
                const dwellSecs = transfer.departSecs - transfer.arriveSecs;

                const warnings = transfer.warnings || [];
                const scheduleWarning = validateScheduleIntersection(transfer, allRoutes, allSchedules);
                if (scheduleWarning && !warnings.includes(scheduleWarning)) {
                    warnings.push(scheduleWarning);
                }

                data.push(({
                    // key: `${route.routeId}|${tripId}|${passingTime.stopTimeId}`,
                    key: transfer.transferId,
                    service: <RouteTitle
                        route={toRoute || {
                            routeId: transfer.toRouteId, routeNumber: transfer.toRouteNumber
                        }} link={true} meta={true}/>,
                    depart: toTime(transfer.departSecs, true),
                    dwellSecs: dwellSecs,
                    dwell: toHrsMinsSecs(dwellSecs),
                    departSecs: transfer.departSecs,
                    routeNumber: transfer.toRouteNumber,
                    transfer: {...transfer, warnings, invalid: scheduleWarning ? true : transfer.invalid},
                    warnings,
                    // isEnabled=true for existing transfers - do full calculation
                    scheduleInfo: getTripScheduleInfo(transfer.toTripId, transfer.toRouteId, allRoutes, allSchedules, setdownSchedules, true),
                }));
                selected.push(transfer.transferId);
            }
        });
        const passingTimes = stop.getPassingTimes(r => r.routeId !== setdownService.transfer.fromRouteId, {
            time: parseInt(setdownService.arriveSecs) - 5 * 60, window: 3600 + 5 * 60, school: true
        });
        passingTimes.forEach(({route, tripId, passingTime}) => {
            const transfer = {
                ...setdownService.transfer,
                toRouteId: route.routeId,
                toRouteNumber: route.routeNumber,
                toColour: route.colour,
                toTripId: tripId,
                toStopId: stop.stopId,
                toStopTimeId: passingTime.stopTimeId,
                inSeat: false,
                transferId: ulid(),
                distance: stop.distance,
                waypoints: stop.waypoints,
                duration: stop.duration,
                warnings: [],
            };
            const existing = findTransfer(transfers, transfer);
            if (existing) {
                // transfer.transferId = existing.transferId
                // selected.push(existing.transferId)
                return;
            }

            const warnings = [];
            const scheduleWarning = validateScheduleIntersection(transfer, allRoutes, allSchedules);
            if (scheduleWarning) {
                warnings.push(scheduleWarning);
            }

            const dwellSecs = passingTime.departSecs - setdownService.arriveSecs;
            data.push(({
                // key: `${route.routeId}|${tripId}|${passingTime.stopTimeId}`,
                key: existing?.transferId || ulid(),
                service: <RouteTitle route={route} link={true} meta={true}/>,
                depart: toTime(passingTime.departSecs, true),
                dwellSecs,
                dwell: toHrsMinsSecs(passingTime.departSecs - setdownService.arriveSecs),
                departSecs: passingTime.departSecs,
                routeNumber: route.routeNumber,
                transfer: {...transfer, warnings, invalid: scheduleWarning ? true : false},
                warnings,
                // isEnabled=false for potential transfers - use fast check only
                scheduleInfo: getTripScheduleInfo(tripId, route.routeId, allRoutes, allSchedules, setdownSchedules, false),
            }));
        });
    };

    const populatePickupServices = useCallback((selectedSetdownServices, isInitialLoad = false) => {
        let pickupData = [], selectedPickups = [];
        selectedSetdownServices.forEach(selectedSetdownService => {
            const service = find(setdownServiceData, ['key', selectedSetdownService]);
            addPickupDataForStop(pickupData, setdownPoint, service, transfers, selectedPickups, allRoutes, allSchedules);
            setdownPoint.linkedStops && setdownPoint.linkedStops.forEach(ls => {
                const stop = new Stop({...allStops[ls.stopId], ...ls});
                stop.setLinkedStops(allStops);
                addPickupDataForStop(pickupData, stop, service, transfers, selectedPickups, allRoutes, allSchedules);
            });
        });
        const uniqueData = uniqWith(pickupData, (d1, d2) => isEqualTransfer(d1.transfer, d2.transfer));
        setPickupServiceData(uniqueData);
        setSelectedPickupServices(selectedPickups);
        // Store original selection on initial load for change detection
        if (isInitialLoad) {
            setOriginalSelectedPickupServices(selectedPickups);
        }
        // Store pending transfers map for new transfers
        const newPendingTransfers = {};
        uniqueData.forEach(row => {
            if (!selectedPickups.includes(row.key)) {
                newPendingTransfers[row.key] = row.transfer;
            }
        });
        setPendingTransfers(newPendingTransfers);
        return pickupData;
    }, [setdownPoint, setdownServiceData, setPickupServiceData, allStops, transfers, allRoutes, allSchedules]);

    useEffect(() => {
        // Pass true for isInitialLoad when transfers data first loads
        const isInitialLoad = originalSelectedPickupServices.length === 0 && transfers?.length > 0;
        populatePickupServices(selectedSetdownServices, isInitialLoad);
    }, [populatePickupServices, transfers, selectedSetdownServices, originalSelectedPickupServices.length]);

    const selectedPickup = useCallback((selectedRowKeys, selectedRows) => {
        // Just update local state - changes will be saved when user clicks Save
        setSelectedPickupServices(selectedRowKeys);
        console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    }, []);

    // Save all pending changes to the database
    const saveChanges = useCallback(async () => {
        setSaving(true);
        try {
            // Find transfers to add (selected now but not originally)
            const toAdd = difference(selectedPickupServices, originalSelectedPickupServices)
                .map(key => {
                    const row = find(pickupServiceData, ['key', key]);
                    if (row) {
                        // Apply any pending in-seat changes
                        const inSeatValue = pendingInSeat.hasOwnProperty(key) ? pendingInSeat[key] : row.transfer.inSeat;
                        return {...row.transfer, inSeat: inSeatValue};
                    }
                    return pendingTransfers[key];
                })
                .filter(t => !!t);

            // Find transfers to remove (originally selected but not now)
            const toRemove = difference(originalSelectedPickupServices, selectedPickupServices);

            // Find transfers with in-seat changes (already existing transfers that were modified)
            const toUpdate = Object.keys(pendingInSeat)
                .filter(key => originalSelectedPickupServices.includes(key) && selectedPickupServices.includes(key))
                .map(key => {
                    const row = find(pickupServiceData, ['key', key]);
                    if (row) {
                        return {...row.transfer, inSeat: pendingInSeat[key]};
                    }
                    return null;
                })
                .filter(t => !!t);

            // Execute all operations
            const promises = [];
            if (toAdd.length > 0) {
                promises.push(transferModelData.save(toAdd));
            }
            if (toRemove.length > 0) {
                promises.push(transferModelData.delete(toRemove, true));
            }
            if (toUpdate.length > 0) {
                promises.push(transferModelData.save(toUpdate));
            }

            await Promise.all(promises);
            console.log('Transfers saved successfully');
        } catch (error) {
            console.error('Error saving transfers:', error);
        } finally {
            setSaving(false);
        }
    }, [selectedPickupServices, originalSelectedPickupServices, pickupServiceData, pendingInSeat, pendingTransfers]);

    // Cancel changes and revert to original state
    const handleCancel = useCallback(() => {
        // Reset to original selection
        setSelectedPickupServices(originalSelectedPickupServices);
        // Clear pending in-seat changes
        setPendingInSeat({});
    }, [originalSelectedPickupServices]);

    // Generate transfers for a specific setdown service
    const autoCreateTransfersForService = useCallback((setdownService) => {
        if (!setdownService) return;
        setGeneratingForService(setdownService.key);

        const fromRouteId = setdownService.transfer?.fromRouteId;
        const fromTripId = setdownService.transfer?.fromTripId;
        let route = allRoutes[fromRouteId];
        if (!route) {
            setGeneratingForService(null);
            return;
        }
        route = new BusRoute(route);
        route.setBaseStops(allStops, route.stopTimes, {excludeCalculateStartEnd: true});

        let newTransfers = route.createTransfers({allStops, defaultWindow: transferWindow});

        // Filter to only transfers from this specific trip AND stop
        const fromStopId = setdownService.transfer?.fromStopId;
        newTransfers = newTransfers.filter(t => t.fromTripId === fromTripId && t.fromStopId === fromStopId);

        // Find similar transfers and remove ones that aren't the shortest distance
        newTransfers = newTransfers.reduce((prev, transfer) => {
            const existingTransfer = prev.find(t => t.fromRouteId === transfer.fromRouteId && t.fromTripId === transfer.fromTripId && t.toRouteId === transfer.toRouteId && t.toTripId === transfer.toTripId && t.fromStopId === transfer.fromStopId);
            if (existingTransfer) {
                if (existingTransfer.distance && (!transfer.distance || transfer.distance < existingTransfer.distance)) {
                    prev.splice(prev.indexOf(existingTransfer), 1, transfer);
                }
            } else {
                prev.push(transfer);
            }
            return prev;
        }, []);
        newTransfers = uniqWith(newTransfers, isEqualTransfer);

        // Filter out transfers where schedules don't intersect
        newTransfers = newTransfers.filter(transfer => {
            const fromRoute = allRoutes[transfer.fromRouteId];
            const toRoute = allRoutes[transfer.toRouteId];
            if (!fromRoute || !toRoute) return false;

            const fromTrip = find(fromRoute?.services, {tripId: transfer.fromTripId});
            const toTrip = find(toRoute?.services, {tripId: transfer.toTripId});
            if (!fromTrip?.scheduleIds?.length || !toTrip?.scheduleIds?.length) return false;

            const sharedScheduleIds = intersection(fromTrip.scheduleIds, toTrip.scheduleIds);
            if (sharedScheduleIds.length > 0) return true;

            const fromSchedules = fromTrip.scheduleIds.map(id => allSchedules[id]).filter(s => !!s);
            const toSchedules = toTrip.scheduleIds.map(id => allSchedules[id]).filter(s => !!s);
            if (!fromSchedules.length || !toSchedules.length) return false;

            const intersectionInfo = getIntersectingDaysInfo(fromSchedules, toSchedules);
            return intersectionInfo && intersectionInfo.count > 0;
        });

        const existing = values(transfers);

        // Separate transfers into: truly new (not in DB or pickupData) and existing but deselected
        const keysToSelect = [];

        // For transfers that exist in DB or pickupServiceData but are deselected, just re-select them
        newTransfers.forEach(transfer => {
            // Find the pickup data for this transfer (whether from DB or local)
            const pickupData = pickupServiceData.find(p =>
                p.transfer && isEqualTransfer(p.transfer, transfer)
            );
            if (pickupData && !selectedPickupServices.includes(pickupData.key)) {
                // It exists in pickup data but is deselected - add it to keys to select
                keysToSelect.push(pickupData.key);
            }
        });

        // For truly new transfers (not in DB), add them to pending and pickupData
        const actualNewTransfers = differenceWith(newTransfers, existing, isEqualTransfer);
        // Also filter out any that already exist in pickupServiceData (to avoid duplicates)
        const filteredNewTransfers = actualNewTransfers.filter(transfer => {
            const alreadyInPickupData = pickupServiceData.find(p =>
                p.transfer &&
                isEqualTransfer(p.transfer, transfer)
            );
            return !alreadyInPickupData;
        });

        const newTransferKeys = filteredNewTransfers.map(t => t.transferId);
        const newPending = {};
        filteredNewTransfers.forEach(t => {
            newPending[t.transferId] = t;
        });
        setPendingTransfers(prev => ({...prev, ...newPending}));

        // Build proper pickup data with display fields
        const newPickupData = filteredNewTransfers.map(t => {
            const toRoute = allRoutes[t.toRouteId];
            const dwellSecs = t.departSecs - t.arriveSecs;
            return {
                key: t.transferId,
                service: <RouteTitle route={toRoute || {routeId: t.toRouteId, routeNumber: t.toRouteNumber}} link={true} meta={true}/>,
                depart: toTime(t.departSecs, true),
                dwellSecs,
                dwell: toHrsMinsSecs(dwellSecs),
                departSecs: t.departSecs,
                routeNumber: t.toRouteNumber,
                transfer: t,
                warnings: [],
                scheduleInfo: {hasIntersection: true},
            };
        });
        setPickupServiceData(prev => [...prev, ...newPickupData]);

        // Select both re-selected DB transfers and new transfers
        setSelectedPickupServices(prev => [...new Set([...prev, ...keysToSelect, ...newTransferKeys])]);

        setGeneratingForService(null);
    }, [allStops, allRoutes, allSchedules, transfers, pickupServiceData, selectedPickupServices, transferWindow]);

    // Calculate per-service transfer counts
    useEffect(() => {
        if (!setdownServiceData?.length || !allRoutes || !allStops || !allSchedules || !transfers) {
            return;
        }

        const timeoutId = setTimeout(() => {
            const counts = {};
            const existing = values(transfers);

            // Build set of currently selected transfer keys for quick lookup
            const selectedKeys = new Set(selectedPickupServices);

            setdownServiceData.forEach(service => {
                const fromRouteId = service.transfer?.fromRouteId;
                const fromTripId = service.transfer?.fromTripId;
                let route = allRoutes[fromRouteId];
                if (!route) {
                    counts[service.key] = 0;
                    return;
                }
                route = new BusRoute(route);
                route.setBaseStops(allStops, route.stopTimes, {excludeCalculateStartEnd: true});

                const fromStopId = service.transfer?.fromStopId;

                let newTransfers = route.createTransfers({allStops, defaultWindow: transferWindow});

                // Filter to only transfers from this specific trip AND stop
                newTransfers = newTransfers.filter(t => t.fromTripId === fromTripId && t.fromStopId === fromStopId);

                // Dedupe
                newTransfers = newTransfers.reduce((prev, transfer) => {
                    const existingTransfer = prev.find(t => t.fromRouteId === transfer.fromRouteId && t.fromTripId === transfer.fromTripId && t.toRouteId === transfer.toRouteId && t.toTripId === transfer.toTripId && t.fromStopId === transfer.fromStopId);
                    if (existingTransfer) {
                        if (existingTransfer.distance && (!transfer.distance || transfer.distance < existingTransfer.distance)) {
                            prev.splice(prev.indexOf(existingTransfer), 1, transfer);
                        }
                    } else {
                        prev.push(transfer);
                    }
                    return prev;
                }, []);
                newTransfers = uniqWith(newTransfers, isEqualTransfer);

                // Filter for schedule intersection
                newTransfers = newTransfers.filter(transfer => {
                    const fromRoute = allRoutes[transfer.fromRouteId];
                    const toRoute = allRoutes[transfer.toRouteId];
                    if (!fromRoute || !toRoute) return false;

                    const fromTrip = find(fromRoute?.services, {tripId: transfer.fromTripId});
                    const toTrip = find(toRoute?.services, {tripId: transfer.toTripId});
                    if (!fromTrip?.scheduleIds?.length || !toTrip?.scheduleIds?.length) return false;

                    const sharedScheduleIds = intersection(fromTrip.scheduleIds, toTrip.scheduleIds);
                    if (sharedScheduleIds.length > 0) return true;

                    const fromSchedules = fromTrip.scheduleIds.map(id => allSchedules[id]).filter(s => !!s);
                    const toSchedules = toTrip.scheduleIds.map(id => allSchedules[id]).filter(s => !!s);
                    if (!fromSchedules.length || !toSchedules.length) return false;

                    const intersectionInfo = getIntersectingDaysInfo(fromSchedules, toSchedules);
                    return intersectionInfo && intersectionInfo.count > 0;
                });

                // Total is all valid transfers (regardless of DB status)
                const total = newTransfers.length;

                // Count how many are available to generate (not in DB and not selected)
                const availableCount = newTransfers.filter(transfer => {
                    // If it exists in DB, it's not available
                    const existsInDb = existing.some(e => isEqualTransfer(e, transfer));
                    if (existsInDb) return false;

                    // If it's selected in local state, it's not available
                    const matchingSelected = pickupServiceData.find(p =>
                        selectedKeys.has(p.key) &&
                        p.transfer &&
                        isEqualTransfer(p.transfer, transfer)
                    );
                    if (matchingSelected) return false;

                    return true;
                }).length;

                // Store both total and available: {total, available}
                counts[service.key] = {total, available: availableCount};
            });

            setPerServiceTransferCounts(counts);
        }, 200);

        return () => clearTimeout(timeoutId);
    }, [setdownServiceData, allRoutes, allStops, allSchedules, transfers, selectedPickupServices, pickupServiceData, transferWindow]);

    const resetTransfers = useCallback(() => {
        // Deselect all pickup services - user must click Save to persist
        setSelectedPickupServices([]);
        setPendingInSeat({});
    }, []);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        saveChanges,
        handleCancel,
        hasChanges,
        resetTransfers,
        isSaving: saving
    }), [saveChanges, handleCancel, hasChanges, resetTransfers, saving]);

    // Setdown table columns - simplified without generate button (moved to middle section)
    const setdownTableColumns = useCallback(() => [{
        title: 'Setdown Service (click to select)',
        dataIndex: 'service',
        key: 'service',
        sorter: (a, b) => a.routeNumber?.localeCompare(b.routeNumber) || -1,
    }, {
        title: 'Arrive',
        dataIndex: 'arrive',
        key: 'arrive',
        width: 90,
        defaultSortOrder: routeId?.length && tripId?.length && stopTimeId?.length ? null : 'ascend',
        sorter: (a, b) => a.arriveSecs - b.arriveSecs,
    }, {
        title: 'Schedule',
        dataIndex: 'scheduleInfo',
        key: 'scheduleInfo',
        width: 85,
        render: (scheduleInfo) => {
            if (!scheduleInfo) return <span style={{color: '#999'}}>-</span>;
            const {scheduleNames, status} = scheduleInfo;
            return (
                <Tooltip title={scheduleNames?.join(', ')}>
                    <Tag color={getScheduleStatusColor(status)} style={{margin: 0}}>
                        {status}
                    </Tag>
                </Tooltip>
            );
        }
    }, {
        title: 'Transfers',
        key: 'transfers',
        width: 80,
        render: (_, record) => {
            const countData = perServiceTransferCounts[record.key];
            if (!countData) return <span style={{color: '#999'}}>...</span>;
            const {total, available} = countData;
            const created = total - available;
            const allDone = available === 0 && total > 0;
            return (
                <span style={{
                    fontWeight: 600,
                    color: allDone ? '#52c41a' : created > 0 ? '#1890ff' : '#8c8c8c'
                }}>
                    {created}/{total}
                </span>
            );
        }
    }], [routeId, tripId, stopTimeId, perServiceTransferCounts]);

    // Get selected setdown service info for display
    const selectedSetdown = setdownServiceData.find(s => selectedSetdownServices.includes(s.key));

    // Get generate button data for selected setdown
    const selectedCountData = selectedSetdown ? perServiceTransferCounts[selectedSetdown.key] : null;
    const isCalculating = selectedCountData === null;
    const total = selectedCountData?.total ?? 0;
    const available = selectedCountData?.available ?? 0;
    const selected = total - available;
    const allSelected = available === 0 && total > 0;

    return (<>
        {!setdownPoint || !allStops || !transfers ? <div><LoadMessage message={'Loading transfers...'}/></div> :
            <div className="transfers-modal transfers-stacked-layout">
                {/* TOP SECTION - Setdown services */}
                <div className="transfers-section setdown-section">
                    <Table
                        className="DataTable"
                        bordered
                        size="small"
                        rowClassName={(record) => selectedSetdownServices.includes(record.key) ? 'ant-table-row-selected' : ''}
                        onRow={(record) => ({
                            onClick: () => {
                                if (!selectedSetdownServices.includes(record.key)) {
                                    setSelectedSetdownServices([record.key]);
                                    populatePickupServices([record.key]);
                                }
                            },
                        })}
                        dataSource={setdownServiceData}
                        columns={setdownTableColumns()}
                        pagination={false}
                        scroll={{y: 180}}
                    />
                </div>

                {/* MIDDLE SECTION - Transfer controls */}
                <div className="transfers-control-section">
                    <div className="transfer-control-group">
                        <span className="control-label">Transfer window:</span>
                        <input
                            type="number"
                            value={transferWindow}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 0 && val <= 120) {
                                    setTransferWindow(val);
                                }
                            }}
                            min={0}
                            max={120}
                            className="transfer-window-input"
                        />
                        <span className="control-label">min</span>
                    </div>
                    <Button
                        type="primary"
                        icon={<ThunderboltOutlined/>}
                        loading={isCalculating || generatingForService === selectedSetdown?.key}
                        disabled={!selectedSetdown || available === 0 || isCalculating}
                        onClick={() => selectedSetdown && autoCreateTransfersForService(selectedSetdown)}
                        className={`transfer-generate-main-btn ${allSelected ? 'all-selected' : ''}`}
                    >
                        {!selectedSetdown ? 'Select a service' : isCalculating ? 'Calculating...' : allSelected ? `All ${total} transfers created` : `Generate ${available} transfer${available !== 1 ? 's' : ''}`}
                    </Button>
                    {selectedSetdown && (
                        <span className="transfer-progress-text">
                            {selected}/{total} created
                        </span>
                    )}
                </div>

                {/* BOTTOM SECTION - Pickup services */}
                <div className="transfers-section pickup-section">
                    <div className="section-header">
                        <h4>
                            Pickup services
                            {selectedSetdown && (
                                <span className="selected-setdown-badge">
                                    for {selectedSetdown.routeNumber} @ {selectedSetdown.arrive}
                                </span>
                            )}
                        </h4>
                        <div className="section-header-right">
                            {(() => {
                                const availableCount = pickupServiceData.filter(p =>
                                    p.scheduleInfo?.hasIntersection !== false && p.dwellSecs >= 0
                                ).length;
                                const unavailableCount = pickupServiceData.length - availableCount;
                                return (
                                    <>
                                        <span className="section-count">
                                            {showAllPickups ? `${pickupServiceData.length} total` : `${availableCount} available`}
                                        </span>
                                        {unavailableCount > 0 && (
                                            <button
                                                className="show-all-toggle"
                                                onClick={() => setShowAllPickups(!showAllPickups)}
                                            >
                                                {showAllPickups ? 'Hide unavailable' : `Show all (+${unavailableCount})`}
                                            </button>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                    <Table
                        className="DataTable"
                        bordered
                        size="small"
                        rowSelection={{
                            type: 'checkbox',
                            columnWidth: 32,
                            selectedRowKeys: selectedPickupServices,
                            onChange: selectedPickup,
                            getCheckboxProps: (record) => ({
                                disabled: saving || record.dwellSecs < 0,
                            })
                        }}
                        dataSource={showAllPickups
                            ? pickupServiceData
                            : pickupServiceData.filter(p => p.scheduleInfo?.hasIntersection !== false && p.dwellSecs >= 0)
                        }
                        columns={pickupTableColumns()}
                        pagination={false}
                        scroll={pickupServiceData.length > 8 ? {y: 320} : undefined}
                    />
                </div>
            </div>}
    </>);
});

export default React.memo(TransfersV2);
